import { useContext, useReducer } from 'react'
import { GlobalContext, GlobalReducer, GlobalState, initState } from './reducer'
import * as GlobalActions from './actions'


/**
 * Instantiates a new `Global` store and returns the state and dispatch in an object for use in a `GlobalContext.Provider`.\
 * Only one Reducer is needed per Provider, and as such, this hook should be used as close to it's designated Provider as possible.
 * ### Use the `useGlobalState` and `useGlobalDispatch` hooks to access Reducer data.
 * 
 * @returns A pre-formatted object a GlobalProvider will accept as a value containing the Reducer's State and Dispatch.
 */
export const useGlobalReducer = () => {
    const [localState, localDispatch] = useReducer(GlobalReducer, initState)
    return { localState, localDispatch }
}

/**
 * Access the nearest `Global` Provider and returns the full state.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns The full `GlobalState` object.
 */
export const useGlobalState = () => {
    const localState = useContext(GlobalContext).localState
    if (!localState) throw new Error('useGlobalState must be used within an GlobalContext Provider')
    return localState
}

/**
 * Access the nearest `Global` Provider and returns it's Dispatch method.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns A Dispatch method that accepts `GlobalActionTypes` for the purposes of updating the Global state.
 */
export const useGlobalDispatch = () => {
    const localDispatch = useContext(GlobalContext).localDispatch
    if (!localDispatch) throw new Error('useGlobalDispatch must be used within an GlobalContext Provider')
    return localDispatch
}


type GlobalSettings = () => {
    /**
     * A copy of the state in the local GlobalReducer.
     */
    state: GlobalState

    setIsIssuingCard: (isIssuingCard: boolean) => void
    setIsServicable: (isServicable: boolean) => void
    setIsUpdateAvailable: (isAvailable: boolean, isCritical: boolean) => void
    setShouldIgnoreUpdateAvailable: (ignore: boolean) => void
    setShowParticipantJoinedModal: (showParticipantJoinedModal: boolean) => void
}

/**
 * Returns an object of pre-formatted utilities that control certain aspects of Global template.
 */
export const useGlobalSettings: GlobalSettings = () => {
    const state = useGlobalState()
    const dispatch = useGlobalDispatch()

    function setIsIssuingCard(isIssuingCard: boolean) {
        dispatch(GlobalActions.setIsIssuingCard(isIssuingCard))
    }

    function setIsServicable(isServicable: boolean) {
        dispatch(GlobalActions.setIsServicable(isServicable))
    }

    function setIsUpdateAvailable(isAvailable: boolean, isCritical: boolean) {
        dispatch(GlobalActions.setIsUpdateAvailable(isAvailable, isCritical))
    }

    function setShouldIgnoreUpdateAvailable(ignore: boolean) {
        dispatch(GlobalActions.setShouldIgnoreUpdateAvilable(ignore))
    }

    function setShowParticipantJoinedModal(showParticipantJoinedModal: boolean) {
        dispatch(GlobalActions.setShowParticipantJoinedModal(showParticipantJoinedModal))
    }

    return {
        setIsIssuingCard,
        setIsServicable,
        setIsUpdateAvailable,
        setShouldIgnoreUpdateAvailable,
        setShowParticipantJoinedModal,
        state,
    }
}
